import React, { useState, useEffect } from 'react';
import { User, X, AlertCircle } from 'lucide-react';
import { impersonationService } from '../services/impersonationService';
import { storage } from '../services/storage';
import { UserProfile } from '../types';
import { useModal } from './ui/ModalProvider';

interface ImpersonationBannerProps {
  onImpersonationStopped: () => void;
}

export const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({ onImpersonationStopped }) => {
  const { toast } = useModal();
  const [stopping, setStopping] = useState(false);
  const [targetUser, setTargetUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    const loadTargetUser = async () => {
      const targetUserId = impersonationService.getTargetUserId();
      if (targetUserId) {
        const profile = await storage.getProfile(targetUserId);
        setTargetUser(profile);
      }
    };
    loadTargetUser();
  }, []);

  const handleStopImpersonation = async () => {
    setStopping(true);
    try {
      const { success, error } = await impersonationService.stopImpersonation();
      
      if (!success) {
        toast.error(error || 'Erreur lors de l\'arrêt de l\'impersonation');
        return;
      }

      toast.success('Retour à votre profil');
      onImpersonationStopped();
    } catch (error: any) {
      toast.error('Erreur lors de l\'arrêt de l\'impersonation: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setStopping(false);
    }
  };

  const displayName = targetUser?.firstName && targetUser?.lastName
    ? `${targetUser.firstName} ${targetUser.lastName}`
    : targetUser?.email || 'Utilisateur';

  return (
    <div className="bg-amber-600 text-white px-4 py-3 flex items-center justify-between shadow-lg z-50 relative">
      <div className="flex items-center gap-3">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <div className="flex items-center gap-2">
          <User className="w-4 h-4" />
          <span className="font-semibold text-sm">
            Vous visualisez en tant que{' '}
            <span className="font-bold">{displayName}</span>
          </span>
        </div>
      </div>
      <button
        onClick={handleStopImpersonation}
        disabled={stopping}
        className="bg-white text-amber-600 hover:bg-amber-50 px-4 py-1.5 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
      >
        {stopping ? (
          <>
            <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Retour...</span>
          </>
        ) : (
          <>
            <X className="w-4 h-4" />
            <span>Revenir à mon profil</span>
          </>
        )}
      </button>
    </div>
  );
};
